/**
 * Central SEO configuration.
 *
 * Every route's title, description and keywords live here, so the site's
 * metadata is generated from ONE place instead of being hand-set on each
 * page. Add a route to `staticSeo` and it is automatically emitted by the
 * <AutoSeo /> component (see src/components/feature/AutoSeo.tsx) and scored
 * in the admin SEO report (/admin/seo).
 *
 * Dynamic routes (e.g. /areas-we-serve/:slug) are resolved from their data
 * source in src/utils/seo.ts.
 */

export interface SeoEntry {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  type?: string;
}

export const staticSeo: Record<string, SeoEntry> = {
  "/": {
    title: "Lahari Packers and Movers Bangalore | Shifting Service",
    description:
      "Lahari Packers and Movers, Bangalore offers safe home and office shifting, packing, storage and warehouse services since 2009. Get a free quote today!",
    keywords:
      "packers and movers bangalore, home shifting bangalore, office relocation bangalore, storage service bangalore",
  },
  "/about": {
    title: "About Lahari Packers and Movers Bangalore",
    description:
      "Learn about Lahari Packers and Movers — a Bangalore relocation company serving families and businesses with safe, on-time shifting since 2009.",
    keywords: "about lahari packers, relocation company bangalore, movers since 2009",
  },
  "/services": {
    title: "Packers and Movers Services in Bangalore | Lahari",
    description:
      "Explore Lahari Packers and Movers services in Bangalore — house shifting, office relocation, local shifting, storage and warehousing at the best price.",
    keywords: "packers and movers services bangalore, house shifting, office shifting, storage bangalore",
  },
  "/services/house-shifting": {
    title: "House Shifting Services in Bangalore | Lahari Packers",
    description:
      "Safe, affordable house shifting in Bangalore with professional packing, loading, moving and unpacking. Get a free home moving quote from Lahari today.",
    keywords: "house shifting bangalore, home shifting services, household movers bangalore",
  },
  "/services/office-shifting": {
    title: "Office Shifting & Relocation in Bangalore | Lahari",
    description:
      "End-to-end office shifting in Bangalore — workstation dismantling, packing, moving and setup with minimal downtime. Request a corporate moving quote.",
    keywords: "office shifting bangalore, office relocation bangalore, corporate movers",
  },
  "/services/storage": {
    title: "Storage Service in Bangalore | Lahari Packers and Movers",
    description:
      "Secure short and long term storage in Bangalore. Store your household or office goods in our monitored warehouse with easy, flexible access.",
    keywords: "storage service bangalore, household storage, warehouse storage bangalore",
  },
  "/services/local-shifting": {
    title: "Local Shifting Services in Bangalore | Lahari Packers",
    description:
      "Fast local shifting in Bangalore for homes and offices. Same-day packers and movers across every locality with transparent, item-wise pricing.",
    keywords: "local shifting bangalore, local packers and movers, same day shifting bangalore",
  },
  "/areas-we-serve": {
    title: "Areas We Serve in Bangalore | Lahari Packers and Movers",
    description:
      "Lahari Packers and Movers serves every major Bangalore locality — Nagarabhavi, Jayanagar, Whitefield, HSR and more. Find packers and movers near you.",
    keywords: "packers and movers bangalore areas, movers near me bangalore, locality shifting",
  },
  "/warehouse": {
    title: "Warehouse Facility in Bangalore | Lahari Packers",
    description:
      "Our secured, well-managed warehouse in Bangalore offers safe storage for household and commercial goods. Short and long term options available.",
    keywords: "warehouse bangalore, storage facility bangalore, secured warehouse",
  },
  "/highlights": {
    title: "Highlights | Lahari Packers and Movers Bangalore",
    description:
      "See what makes Lahari Packers and Movers a trusted choice in Bangalore — experience, trained crews, quality packing material and thousands of happy moves.",
    keywords: "lahari packers highlights, why choose lahari, best movers bangalore",
  },
  "/gallery": {
    title: "Image Gallery | Lahari Packers and Movers Bangalore",
    description:
      "Browse photos of our packing, loading and moving work across Bangalore. Real moves by Lahari Packers and Movers, from homes to offices.",
    keywords: "packers and movers gallery, moving photos bangalore, lahari gallery",
  },
  "/contact": {
    title: "Contact Lahari Packers and Movers Bangalore",
    description:
      "Get in touch with Lahari Packers and Movers Bangalore. Call, WhatsApp or send us your shifting requirement and our team will respond quickly.",
    keywords: "contact packers and movers bangalore, lahari contact, movers phone number bangalore",
  },
  "/request-a-quote": {
    title: "Get a Free Moving Quote | Lahari Packers Bangalore",
    description:
      "Request a free, item-wise shifting quote from Lahari Packers and Movers Bangalore. Transparent pricing for house, office and local moves — no obligation.",
    keywords: "free moving quote bangalore, shifting cost bangalore, movers estimate",
  },
  "/terms": {
    title: "Terms and Conditions | Lahari Packers and Movers",
    description:
      "Read the terms and conditions for using Lahari Packers and Movers services and website. Clear, fair policies for every shifting booking.",
    keywords: "lahari packers terms, moving terms and conditions",
  },
  "/privacy": {
    title: "Privacy Policy | Lahari Packers and Movers",
    description:
      "Learn how Lahari Packers and Movers collects, uses and protects your personal information when you enquire or book a shifting service.",
    keywords: "lahari privacy policy, movers data protection",
  },
};